// Configuration templates and one-click setup scripts for IDE / CLI integrations.
// Used by the API key page (/console/api) "view config file" and "one-click script" features.

export const CONFIG_BASE_URL = 'https://api.etherworks.cn';
export const CONFIG_API_KEY_PLACEHOLDER = '<YOUR_API_KEY>';
export const CONFIG_MODEL_PLACEHOLDER = 'your-model-id';

export type ScriptPlatform = 'bash' | 'powershell';

export interface IdeConfigFile {
  /** Display path, e.g. ~/.codex/config.toml */
  path: string;
  /** Code block language hint, e.g. toml | json | yaml */
  language: string;
  content: string;
}

export interface IdeEnvVar {
  name: string;
  value: string;
}

export interface IdeKvRow {
  label: string;
  value: string;
}

export interface IdeToolConfig {
  id: string;
  name: string;
  logo: string;
  endpoint: string;
  /** Config files written to disk (empty for UI-only tools). */
  files: IdeConfigFile[];
  /** Environment variables required by the tool (e.g. Codex). */
  envVars: IdeEnvVar[];
  /** Key/value parameters for UI-only tools (Cursor, Cline). */
  table: IdeKvRow[];
  /** Whether a one-click setup script can be generated. */
  supportsScript: boolean;
}

export interface BuildConfigParams {
  baseUrl?: string;
  apiKey: string;
  modelId: string;
}

interface ResolvedParams {
  baseUrl: string;
  apiKey: string;
  modelId: string;
}

function codexConfigFile(p: ResolvedParams): IdeConfigFile {
  return {
    path: '~/.codex/config.toml',
    language: 'toml',
    content: `model = "${p.modelId}"
model_provider = "etherworks"

[model_providers.etherworks]
name = "EtherWorks"
base_url = "${p.baseUrl}/v1"
env_key = "ETHERWORKS_API_KEY"
wire_api = "responses"
requires_openai_auth = false`,
  };
}

function claudeConfigFile(p: ResolvedParams): IdeConfigFile {
  return {
    path: '~/.claude/settings.json',
    language: 'json',
    content: JSON.stringify(
      {
        env: {
          ANTHROPIC_BASE_URL: p.baseUrl,
          ANTHROPIC_AUTH_TOKEN: p.apiKey,
          ANTHROPIC_MODEL: p.modelId,
          ANTHROPIC_SMALL_FAST_MODEL: p.modelId,
          API_TIMEOUT_MS: '3000000',
          CLAUDE_CODE_DISABLE_NONESSENTIAL_TRAFFIC: '1',
        },
      },
      null,
      2,
    ),
  };
}

function opencodeConfigFile(p: ResolvedParams): IdeConfigFile {
  return {
    path: '~/.config/opencode/opencode.json',
    language: 'json',
    content: JSON.stringify(
      {
        provider: {
          openai: {
            npm: '@ai-sdk/openai',
            name: 'EtherWorks',
            options: {
              baseURL: `${p.baseUrl}/v1`,
              apiKey: p.apiKey,
            },
            models: {
              [p.modelId]: { name: p.modelId },
            },
          },
        },
      },
      null,
      2,
    ),
  };
}

function openAiTableRows(p: ResolvedParams): IdeKvRow[] {
  return [
    { label: 'base_url', value: `${p.baseUrl}/v1` },
    { label: 'api_key', value: p.apiKey },
    { label: 'model', value: p.modelId },
  ];
}

export function buildIdeTools(params: BuildConfigParams): IdeToolConfig[] {
  const p: ResolvedParams = {
    baseUrl: (params.baseUrl ?? CONFIG_BASE_URL).replace(/\/+$/, ''),
    apiKey: params.apiKey,
    modelId: params.modelId,
  };

  return [
    {
      id: 'codex',
      name: 'Codex',
      logo: '/logos/ide/openai.svg',
      endpoint: '/v1/responses',
      files: [codexConfigFile(p)],
      envVars: [{ name: 'ETHERWORKS_API_KEY', value: p.apiKey }],
      table: [],
      supportsScript: true,
    },
    {
      id: 'claude-code',
      name: 'Claude Code',
      logo: '/logos/ide/claude.svg',
      endpoint: '/v1/messages',
      files: [claudeConfigFile(p)],
      envVars: [],
      table: [],
      supportsScript: true,
    },
    {
      id: 'opencode',
      name: 'OpenCode',
      logo: '/logos/ide/opencode.svg',
      endpoint: '/v1/responses',
      files: [opencodeConfigFile(p)],
      envVars: [],
      table: [],
      supportsScript: true,
    },
    {
      id: 'cursor',
      name: 'Cursor',
      logo: '/logos/ide/cursor.svg',
      endpoint: '/v1/chat/completions',
      files: [],
      envVars: [],
      table: openAiTableRows(p),
      supportsScript: false,
    },
    {
      id: 'cline',
      name: 'Cline',
      logo: '/logos/ide/cline.svg',
      endpoint: '/v1/chat/completions',
      files: [],
      envVars: [],
      table: openAiTableRows(p),
      supportsScript: false,
    },
  ];
}

function toHomePath(displayPath: string): string {
  return displayPath.replace(/^~\//, '$HOME/');
}

function buildBashScript(tool: IdeToolConfig): string {
  const lines: string[] = [
    '#!/usr/bin/env bash',
    'set -euo pipefail',
    '',
    `# EtherWorks ${tool.name} setup script`,
    '',
  ];

  for (const file of tool.files) {
    const target = toHomePath(file.path);
    lines.push(
      `TARGET="${target}"`,
      'mkdir -p "$(dirname "$TARGET")"',
      'if [ -f "$TARGET" ]; then',
      '  cp "$TARGET" "$TARGET.bak.$(date +%Y%m%d%H%M%S)"',
      '  echo "Backed up existing $TARGET"',
      'fi',
      "cat > \"$TARGET\" <<'ETHERWORKS_EOF'",
      file.content,
      'ETHERWORKS_EOF',
      'echo "Wrote $TARGET"',
      '',
    );
  }

  for (const env of tool.envVars) {
    lines.push(
      'RC_FILE="$HOME/.bashrc"',
      'if [ -n "${ZSH_VERSION:-}" ] || [ "$(basename "${SHELL:-}")" = "zsh" ]; then RC_FILE="$HOME/.zshrc"; fi',
      `if ! grep -q 'export ${env.name}=' "$RC_FILE" 2>/dev/null; then`,
      `  echo 'export ${env.name}="${env.value}"' >> "$RC_FILE"`,
      `  echo "Added ${env.name} to $RC_FILE"`,
      'else',
      `  echo "${env.name} already present in $RC_FILE, skipped"`,
      'fi',
      `export ${env.name}="${env.value}"`,
      '',
    );
  }

  lines.push('echo "EtherWorks configuration complete."');
  return lines.join('\n');
}

function buildPowerShellScript(tool: IdeToolConfig): string {
  const lines: string[] = [
    `# EtherWorks ${tool.name} setup script`,
    "$ErrorActionPreference = 'Stop'",
    '',
  ];

  for (const file of tool.files) {
    const target = toHomePath(file.path);
    lines.push(
      `$target = "${target}"`,
      '$dir = Split-Path -Parent $target',
      'if (-not (Test-Path $dir)) { New-Item -ItemType Directory -Path $dir -Force | Out-Null }',
      'if (Test-Path $target) {',
      '  Copy-Item $target "$target.bak.$(Get-Date -Format yyyyMMddHHmmss)"',
      '  Write-Host "Backed up existing $target"',
      '}',
      "$content = @'",
      file.content,
      "'@",
      'Set-Content -Path $target -Value $content -Encoding UTF8',
      'Write-Host "Wrote $target"',
      '',
    );
  }

  for (const env of tool.envVars) {
    lines.push(
      `[Environment]::SetEnvironmentVariable('${env.name}', '${env.value}', 'User')`,
      `$env:${env.name} = '${env.value}'`,
      `Write-Host "Set user environment variable ${env.name}"`,
      '',
    );
  }

  lines.push('Write-Host "EtherWorks configuration complete."');
  return lines.join('\n');
}

export function buildSetupScript(tool: IdeToolConfig, platform: ScriptPlatform): string {
  return platform === 'bash' ? buildBashScript(tool) : buildPowerShellScript(tool);
}

export function scriptFileName(tool: IdeToolConfig, platform: ScriptPlatform): string {
  return `etherworks-${tool.id}-setup.${platform === 'bash' ? 'sh' : 'ps1'}`;
}
